/**
 * Connected-services (remote-MCP connector) list + revoke — a paired-gated,
 * web-local Settings `extraSection`. No approve flow: the 6-digit approve is
 * phone-only, so the empty state points the user at their phone.
 */

import { Box, Button, formatRelativeTime, Spinner, Text } from "@reflecto/client-ui";
import { useCallback, useEffect, useState } from "react";
import { webStorage } from "../adapters/storage.web";
import { type ConnectorSummary, listConnectors, revokeConnector } from "../api/client";
import { messages } from "../i18n";
import { logWarn } from "../lib/log";

const C = messages.connectors;

type LoadState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; connectors: ConnectorSummary[] };

export function Connectors() {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const { token } = await webStorage.get("token");
      if (!token) {
        // Not paired / token cleared mid-flight — render an empty list rather
        // than an error; the section is paired-gated by the host anyway.
        setState({ status: "ready", connectors: [] });
        return;
      }
      const connectors = await listConnectors(token);
      setState({ status: "ready", connectors });
    } catch (err) {
      logWarn("ui", "connectors_load_failed", err);
      setState({ status: "error" });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRevoked = useCallback((id: string) => {
    // Optimistic removal — drop the revoked row locally; no refetch.
    setState((prev) =>
      prev.status === "ready"
        ? { status: "ready", connectors: prev.connectors.filter((c) => c.id !== id) }
        : prev
    );
  }, []);

  if (state.status === "loading") {
    return (
      <Box paddingY="md" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (state.status === "error") {
    return (
      <Box direction="column" gap="sm" paddingY="sm">
        <Text size="s" tone="negative">
          {C.loadError}
        </Text>
        <Box>
          <Button variant="secondary" size="s" onClick={() => void load()}>
            {C.retry}
          </Button>
        </Box>
      </Box>
    );
  }

  if (state.connectors.length === 0) {
    return (
      <Text as="p" size="s" tone="tertiary">
        {C.emptyBody}
      </Text>
    );
  }

  return (
    <Box direction="column" gap="md">
      {state.connectors.map((connector) => (
        <ConnectorRow key={connector.id} connector={connector} onRevoked={handleRevoked} />
      ))}
    </Box>
  );
}

interface ConnectorRowProps {
  connector: ConnectorSummary;
  onRevoked: (id: string) => void;
}

function ConnectorRow({ connector, onRevoked }: ConnectorRowProps) {
  const [confirming, setConfirming] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRevoke = useCallback(async () => {
    setError(null);
    setRevoking(true);
    try {
      const { token } = await webStorage.get("token");
      if (!token) throw new Error("missing token"); // surface a retryable error, not a silent no-op
      await revokeConnector(token, connector.id);
      // Collapse confirm before the parent unmounts the row (`revoking` stays true — row is leaving).
      setConfirming(false);
      onRevoked(connector.id);
    } catch (err) {
      logWarn("ui", "connector_revoke_failed", err);
      setError(C.revokeError);
      setRevoking(false);
      setConfirming(false);
    }
  }, [connector.id, onRevoked]);

  // Friendly label only when `notifications:send` is the SOLE scope; show the raw string for any
  // multi-scope grant (the server is multi-scope by design) so a co-granted scope isn't swallowed.
  const scopeTokens = connector.scopes.split(" ").filter(Boolean);
  const scopeText =
    scopeTokens.length === 1 && scopeTokens[0] === "notifications:send"
      ? C.scopeSend
      : connector.scopes;

  // Server timestamps are unix SECONDS; the formatters expect ms.
  const addedText = C.added(formatAbsoluteDate(connector.createdAt * 1000));
  const lastUsedText =
    connector.lastUsedAt != null
      ? C.lastUsed(formatRelativeTime(connector.lastUsedAt * 1000))
      : C.neverUsed;

  return (
    <Box direction="column" gap="xs">
      <Box align="center" justify="between" gap="sm">
        <Box direction="column" gap="xs" style={{ minWidth: 0, flex: 1 }}>
          <Text size="s" weight="medium">
            {connector.label}
          </Text>
          {scopeText.length > 0 && (
            <Text size="xs" tone="tertiary">
              {scopeText}
            </Text>
          )}
        </Box>
        {!confirming && (
          <Button
            variant="ghost"
            size="s"
            disabled={revoking}
            onClick={() => {
              setError(null); // drop any prior failure so the reopened confirm starts clean
              setConfirming(true);
            }}
          >
            {C.revoke}
          </Button>
        )}
      </Box>

      <Text size="xs" tone="tertiary">
        {`${addedText} · ${lastUsedText}`}
      </Text>

      {confirming && (
        <Box direction="column" gap="sm">
          <Text size="s" tone="secondary">
            {C.revokeConfirm(connector.label)}
          </Text>
          <Box gap="sm">
            <Button
              variant="negative"
              size="s"
              loading={revoking}
              disabled={revoking}
              // Disambiguate from other rows' Disconnect buttons for screen readers.
              aria-label={C.revokeConfirmLabel(connector.label)}
              onClick={() => void handleRevoke()}
            >
              {C.revokeAction}
            </Button>
            <Button
              variant="secondary"
              size="s"
              disabled={revoking}
              onClick={() => setConfirming(false)}
            >
              {C.cancel}
            </Button>
          </Box>
        </Box>
      )}

      {error && (
        // role="alert": the focused confirm button unmounts on failure, so announce the error.
        <Text size="xs" tone="negative" role="alert">
          {error}
        </Text>
      )}
    </Box>
  );
}

/** Locale "Mon DD" for the connector's creation date. */
function formatAbsoluteDate(epochMs: number): string {
  return new Date(epochMs).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
